import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { EventOfType, EventPayloadInput, EventType, LedgerEvent, RegisterSettings } from "@shared/events";
import { makeEvent } from "@shared/events";
import type { RegisterState } from "@shared/ledger";
import { DEFAULT_SETTINGS, applyEvent, emptyState, foldEvents } from "@shared/ledger";
import { uuidv7 } from "@shared/ids";
import type { JoinRequest, Role, Tier } from "@shared/api";
import type { Backup, LocalStore, SyncClient, SyncStatus } from "@client/db/contract";

/**
 * OWNED BY C1. The one place the UI reads register state and writes events.
 *
 * Everything is a ledger (PRD §5.5): `dispatch` builds an event with `makeEvent`, writes it to the
 * LocalStore *before* returning (§5.2 "never lose a sale"), and folds it into the in-memory state so
 * the screen repaints in the same tick (U-008, <50 ms). Sync is a background concern: the SyncClient is
 * observed for a status badge and nothing in this module ever awaits the network.
 *
 * Dependencies are injected (C4 implements LocalStore/SyncClient/Backup), so tests run against
 * createMemoryStore() with no IndexedDB and no server.
 */

export interface RegisterContextValue {
  /** Folded ledger state. Replaced by a new object on every change. */
  state: RegisterState;
  settings: RegisterSettings;
  /** Append one event. Resolves once it is durably stored. */
  dispatch: <T extends EventType>(type: T, payload: EventPayloadInput<T>) => Promise<EventOfType<T>>;
  syncStatus: SyncStatus;
  /** Events written locally but not yet accepted by the server. */
  pendingCount: number;
  registerId: string;
  deviceId: string;
  role: Role;
  tier: Tier;
  /** True once the initial load from the LocalStore has completed. */
  ready: boolean;
  /**
   * D-070, D-073: a cashier device whose register owner is not on Pro, or an owner whose account has no
   * plan. It shows what is stored but records nothing (`dispatch` rejects) and does not sync; unsent events
   * stay on the device until access returns.
   */
  readOnly: boolean;
  store: LocalStore;
  backup: Backup | null;
  /** Re-read everything from the store. */
  reload: () => Promise<void>;
  /**
   * D-077: devices waiting for the owner's approval to join any of the account's registers, as the sync
   * socket last said. Always empty for a cashier, a read-only device, or without sync.
   */
  joinRequests: JoinRequest[];
}

const RegisterContext = createContext<RegisterContextValue | null>(null);

/** What `dispatch` rejects with on a read-only device (D-070). */
export const READ_ONLY_MESSAGE = "This register is read-only on this device, so nothing can be recorded.";

export interface RegisterProviderProps {
  registerId: string;
  settings?: RegisterSettings;
  store: LocalStore;
  sync?: SyncClient | null;
  backup?: Backup | null;
  role?: Role;
  tier?: Tier;
  readOnly?: boolean;
  newId?: () => string;
  now?: () => number;
  children: ReactNode;
}

export function RegisterProvider({
  registerId, settings, store, sync = null, backup = null, role = "owner", tier = "lite", readOnly = false,
  newId = uuidv7, now = Date.now, children,
}: RegisterProviderProps) {
  const [state, setState] = useState<RegisterState>(() => emptyState(registerId, settings ?? DEFAULT_SETTINGS));
  const [ready, setReady] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(() => sync?.status() ?? "offline");
  const [deviceId, setDeviceId] = useState("");
  const [joinRequests, setJoinRequests] = useState<JoinRequest[]>([]);
  /** Serialises seq allocation so two taps in the same tick never collide. */
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const baseSettings = settings ?? DEFAULT_SETTINGS;

  const reload = useCallback(async () => {
    await store.open();
    const meta = await store.getMeta();
    setDeviceId(meta.deviceId);
    const stored = await store.loadEvents(registerId);
    setState(foldEvents(stored.map((s) => s.event), emptyState(registerId, baseSettings)));
    setPendingCount((await store.pendingEvents(registerId)).length);
    setReady(true);
  }, [store, registerId, baseSettings]);

  useEffect(() => {
    let live = true;
    void reload().catch(() => { if (live) setReady(true); });
    const unsubscribe = store.subscribe(() => { void reload(); });
    return () => { live = false; unsubscribe(); };
  }, [reload, store]);

  useEffect(() => {
    // D-070: a read-only device does not sync; the server would refuse it anyway.
    if (!sync || readOnly) return;
    sync.start(registerId);
    setSyncStatus(sync.status());
    const off = sync.subscribe(setSyncStatus);
    return () => { off(); sync.stop(); };
  }, [sync, registerId, readOnly]);

  useEffect(() => {
    if (!sync?.subscribeJoinRequests || readOnly || role !== "owner") return;
    const off = sync.subscribeJoinRequests(setJoinRequests);
    return () => { off(); setJoinRequests([]); };
  }, [sync, readOnly, role]);

  const dispatch = useCallback(<T extends EventType>(type: T, payload: EventPayloadInput<T>) => {
    // The screens hide every action on a read-only device; this is the backstop (D-070).
    if (readOnly) return Promise.reject(new Error(READ_ONLY_MESSAGE));
    const run = chain.current.then(async () => {
      const meta = await store.getMeta();
      const event = makeEvent(type, payload, {
        registerId,
        deviceId: meta.deviceId,
        seq: meta.nextSeq,
        occurredAt: now(),
      }, newId);
      await store.appendEvents([event as LedgerEvent]);
      // Optimistic fold: the screen updates now; the store's subscribe callback re-folds from disk after.
      setState((prev) => applyEvent(prev, event as LedgerEvent));
      setPendingCount((n) => n + 1);
      void sync?.flush().catch(() => {});
      return event;
    });
    chain.current = run.catch(() => {});
    return run;
  }, [store, registerId, now, newId, sync, readOnly]);

  const value = useMemo<RegisterContextValue>(() => ({
    state,
    settings: state.settings,
    dispatch,
    syncStatus,
    pendingCount,
    registerId,
    deviceId,
    role,
    tier,
    ready,
    readOnly,
    store,
    backup,
    reload,
    joinRequests,
  }), [state, dispatch, syncStatus, pendingCount, registerId, deviceId, role, tier, ready, readOnly, store, backup, reload, joinRequests]);

  return <RegisterContext.Provider value={value}>{children}</RegisterContext.Provider>;
}

export function useRegister(): RegisterContextValue {
  const ctx = useContext(RegisterContext);
  if (!ctx) throw new Error("useRegister must be used inside <RegisterProvider>");
  return ctx;
}

/** Non-throwing variant for components that can render without a register (the shell chrome). */
export function useRegisterOrNull(): RegisterContextValue | null {
  return useContext(RegisterContext);
}

/**
 * The register's currency is locked once it has recorded a sale (F-201): changing it would reinterpret
 * every stored minor-unit amount.
 */
export function hasRecordedSale(state: RegisterState): boolean {
  for (const t of Object.values(state.transactions)) if (t.kind === "sale") return true;
  for (const d of Object.values(state.days)) if (d.salesCount > 0) return true;
  return false;
}

/** Active (non-archived) products in sort order. */
export function activeProducts(state: RegisterState) {
  return Object.values(state.products)
    .filter((p) => p.archivedAt === null)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
}

export function archivedProducts(state: RegisterState) {
  return Object.values(state.products)
    .filter((p) => p.archivedAt !== null)
    .sort((a, b) => (b.archivedAt ?? 0) - (a.archivedAt ?? 0));
}

/** True if any recorded transaction references this product. Deleting a product is only offered for an
 * archived product with no sales, so its history stays intact (F-305). */
export function productHasSales(state: RegisterState, productId: string): boolean {
  for (const t of Object.values(state.transactions)) {
    if (t.items.some((it) => it.productId === productId)) return true;
  }
  return false;
}
